import * as Papa from "papaparse";
import { defaultServiceContext } from "@app/contexts";
import DesignerSetting from "@app/DesignerSetting";
import { useUserSettings } from "@app/hooks";
import MetaData from "@app/models/MetaDataModel";
import { AvsFileName, ObjectClass } from "@app/types";
import Utility from '@app/utilities/Utility';
import { BreakingChangesDialog } from "@app/widgets/dialogs";
import { ExportManager } from './ExportManager';
import Exporter from './Exporter';
import { getDsids, getEnvironmentInfo } from "./functions";

/**
 * JSDoc imports.
 *
 * @import {
 *   AvsChangeLogs,
 *   BehaviorSubject,
 *   Design,
 *   LoadingProgressStatus
 * } from "@app/types";
 */

const {
    avsService,
    errorMessageService,
    globalModalsService,
    intentService
} = defaultServiceContext;

export class SaveToRepository {
    /**
     * assume that components without moduleId should belong to the host CoMo (serialized._id, etc.)
     * @param {json} serialized 
     */
    static tagNativeSerializedComponents(serialized) {
        const native_objects = serialized.components.objects.filter(obj => !obj.props.moduleId);
        native_objects.forEach(obj => {
            obj.props.version = serialized.repoVersion;
            obj.props.moduleId = serialized._id;
            obj.props.moduleApp = serialized.application;
        });

        const native_modules = serialized.components.modules.filter(mod => !mod.props.moduleId) || [];
        native_modules.forEach(mod => {
            mod.props.version = serialized.repoVersion;
            mod.props.moduleId = serialized._id;
            mod.props.moduleApp = serialized.application;
            for (let k = 0; k < mod.versions[1].flow.nodes.length; k++) {
                let nod = mod.versions[1].flow.nodes[k];
                nod.props.version = serialized.repoVersion;
                nod.props.moduleId = serialized._id;
                nod.props.moduleApp = serialized.application;
            }
        });

        const native_intets = serialized.components.intents.filter(int => !int.props.moduleId) || [];
        native_intets.forEach(int => {
            int.props.version = serialized.repoVersion;
            int.props.moduleId = serialized._id;
            int.props.moduleApp = serialized.application;
        })

        const native_functions = (serialized.functions) ? serialized.functions.filter(func => !func.moduleId) : [];
        native_functions.forEach(func => {
            func.moduleId = serialized._id;
            func.version = serialized.repoVersion;
            func.moduleApp = serialized.application;
        });

        const native_csvData = (serialized.csvData) ? serialized.csvData.filter(csv => !csv.moduleId) : [];
        native_csvData.forEach(csv => {
            csv.moduleId = serialized._id;
            csv.version = serialized.repoVersion;
            csv.moduleApp = serialized.application;
        });

        const native_graphQLSchemas = (serialized.graphQLSchemas) ? serialized.graphQLSchemas.filter(gql => !gql.moduleId) : [];
        native_graphQLSchemas.forEach(gql => {
            gql.version = serialized.repoVersion;
            gql.moduleId = serialized._id;
            gql.moduleApp = serialized.application;
        });
    }
    
    /**
     * @param {Design} serialized
     * @param {AvsChangeLogs[] | null} changeMapData
     * @param {BehaviorSubject<LoadingProgressStatus>} progressMonitor Reports upload progress.
     * @param {Design | null} [savedVersion] Version to check for breaking changes against.
     */
    static async saveToRepoDesign(
        serialized,
        changeMapData,
        progressMonitor,
        savedVersion = null
    ) {
        const timeId = 'Save To Repo Design';
        console.time(timeId);
        const isCollab = DesignerSetting.s.is_collab !== "false";
        const {
            branchId,
            branchName,
            clientId,
            designId,
            designName
        } = serialized;

        if (changeMapData) {
            changeMapData = changeMapData.slice(0, 20);
        }

        if (
            isCollab
            && savedVersion
            && BreakingChangesDialog.popupIfBreakingChangesExist({
                currentVersion: serialized,
                savedVersion
            })
        ) {
            console.timeEnd(timeId);
            return null;
        }

        const exportManager = new ExportManager();
        const functionObj = Exporter.renameReusableFunction(serialized);

        const { saveShouldYield } = useUserSettings.getState();

        await Exporter.cleanSerialized(serialized, saveShouldYield);

        // do not modify serialized when passing to ExportManager, instead pass in a copy of serialized
        const exportObj = await exportManager.getExport(serialized, { shouldYield: saveShouldYield });

        serialized.author = DesignerSetting.s.author;

        if (!designId) {
            const client = await avsService.getClient(clientId);
            const designId = client.applications.find((app) => (
                app.name === designName
                || app.name.slice(client.name.length) === designName
            ))?.id;
            const avsDesign = await avsService.createDesign(
                clientId,
                { id: designId, name: designName }
            );
            serialized.designId = avsDesign.id;
            await avsService.createBranch(avsDesign.id, {
                d2Client: serialized.clientName,
                d2Design: serialized.clientName + designName,
                id: branchId,
                name: branchName
            });
        }

        const savedSerialized = await SaveToRepository.saveRepo(
            serialized,
            exportObj,
            functionObj,
            changeMapData,
            progressMonitor
        );
        console.timeEnd(timeId);
        return savedSerialized;
    }

    // Returns if link source/target is null or cnnot be found in nodes array
    static getIsOrphanedLink(flow, link) {
        // if link source/target do not exist in nodes, or if source/target value is null delete from design
        var nodes = flow.nodes;
        var sourceNotFound = !link.source || nodes.findIndex(n => n.id === link.source) === -1;
        var targetNotFound = !link.target || nodes.findIndex(n => n.id === link.target) === -1;

        return (sourceNotFound || targetNotFound);

    }

    /**
     * @param {Design} serialized
     * @param {*} exportObj
     * @param {*} functionObj
     * @param {*} changeMapData
     * @param {BehaviorSubject<LoadingProgressStatus>} progressMonitor Reports upload progress.
     */
    static async saveRepo(
        serialized,
        exportObj,
        functionObj,
        changeMapData,
        progressMonitor
    ) {
        const isCommonModule = Utility.getIsCommonModule(serialized);

        // const hasChangeData = (changeMapData) ? Object.keys(changeMapData[0].changeLogMap[0]).length > 0: false;
        // TODO: instead of looping through all design items, loop over changeMap entries;
        if (serialized) {
            if (isCommonModule) {
                // see https://jira.interactions.net/browse/DEVDES2-1851
                if(!serialized.hasOwnProperty("applicationVersion")){
                    serialized.applicationVersion = "undefined"
                }
                SaveToRepository.tagNativeSerializedComponents(serialized);
            }

            for (let i = 0; i < serialized.components.objects.length; i++) {
                serialized.components.objects[i].props.lastSavedChange = serialized.components.objects[i].props.lastLocalChange;
            }
            for (let i = 0; i < serialized.components.modules.length; i++) {

                serialized.components.modules[i].props.lastSavedChange = serialized.components.modules[i].props.lastLocalChange;

                for (let k = 0; k < serialized.components.modules[i].versions[1].flow.nodes.length; k++) {
                    serialized.components.modules[i].versions[1].flow.nodes[k].props.lastSavedChange =
                        serialized.components.modules[i].versions[1].flow.nodes[k].props.lastLocalChange;

                }
                for (let l = 0; l < serialized.components.modules[i].versions[1].flow.links.length; l++) {

                    // if link source/target do not exist in nodes, or if source/target value is null delete from design
                    if (SaveToRepository.getIsOrphanedLink(serialized.components.modules[i].versions[1].flow, serialized.components.modules[i].versions[1].flow.links[l])) {
                        serialized.components.modules[i].versions[1].flow.links.splice(l, 1);

                    } else { // update lastSavedChange
                        serialized.components.modules[i].versions[1].flow.links[l].props.lastSavedChange =
                            serialized.components.modules[i].versions[1].flow.links[l].props.lastLocalChange;

                        delete serialized.components.modules[i].versions[1].flow.links[l].props.sourceChangedTime;
                        delete serialized.components.modules[i].versions[1].flow.links[l].props.targetChangedTime;
                    }
                }
            }
            for (let i = 0; i < serialized.components.intents.length; i++) {
                serialized.components.intents[i].props.lastSavedChange = serialized.components.intents[i].props.lastLocalChange;
            }
            for (let type in serialized.fillerPrompts) {
                for (let df = 0; df < serialized.fillerPrompts[type].length; df++) {
                    serialized.fillerPrompts[type][df].lastSavedChange = serialized.fillerPrompts[type][df].lastLocalChange;
                }
            }
            for (let i = 0; i < serialized.components.variables.length; i++) {
                serialized.components.variables[i].props.lastSavedChange = serialized.components.variables[i].props.lastLocalChange;

            }
            if (serialized.functions && serialized.functions.length > 0) {
                for (let f = 0; f < serialized.functions.length; f++) {
                    serialized.functions[f].lastSavedChange = serialized.functions[f].lastLocalChange;

                }
            }
            if (serialized.csvData && serialized.csvData.length > 0) {
                for (let f = 0; f < serialized.csvData.length; f++) {
                    serialized.csvData[f].lastSavedChange = serialized.csvData[f].lastLocalChange;
                }
            }
            if (serialized.graphQLSchemas && serialized.graphQLSchemas.length > 0) {
                for (let f = 0; f < serialized.graphQLSchemas.length; f++) {
                    serialized.graphQLSchemas[f].lastSavedChange = serialized.graphQLSchemas[f].lastLocalChange;
                }
            }
            if (serialized.globalExceptionHandling) {
                serialized.globalExceptionHandling.lastSavedChange = serialized.globalExceptionHandling.lastLocalChange;
            }
            if (serialized.globalIntents && serialized.globalIntents.length > 0) {
                for (let f = 0; f < serialized.globalIntents.length; f++) {
                    serialized.globalIntents[f].lastSavedChange = serialized.globalIntents[f].lastLocalChange;
                }
            }
            if (serialized.compoundIntents && serialized.compoundIntents.length > 0) {
                for (let f = 0; f < serialized.compoundIntents.length; f++) {
                    serialized.compoundIntents[f].lastSavedChange = serialized.compoundIntents[f].lastLocalChange;
                }
            }
            if (serialized.globalErrorHandling) {
                serialized.globalErrorHandling.lastSavedChange = serialized.globalErrorHandling.lastLocalChange;
            }
            if (serialized.languagesMeta) {
                serialized.languagesMeta['lastSavedChange'] = serialized.languagesMeta['lastLocalChange'] || window.apm.timestamp;
            }
            if(serialized.applicationVersionLastChange) {
                serialized.applicationVersionLastChange = window.apm.timestamp
            }
            if (serialized.appProperties && serialized.appProperties.length > 0) {
                for (var f = 0; f < serialized.appProperties.length; f++) {
                    serialized.appProperties[f].lastSavedChange = serialized.appProperties[f].lastLocalChange;
                }
            }
            if (serialized.unitTests) {
                for (let i = 0; i < serialized.unitTests.length; i++) {
                    const ut = serialized.unitTests[i];
                    if (!ut.createdTime) {
                        ut.createdTime = window.apm.timestamp;
                    }
                    if (!ut.lastLocalChange) {
                        ut.lastLocalChange = window.apm.timestamp;
                    }
                    ut.lastSavedChange = ut.lastLocalChange;
                }
            }
            if(serialized.folders && serialized.folders.modules){
                serialized.folders.modules.forEach(folder => {
                    folder.props.lastSavedChange = folder.props.lastLocalChange;
                });
            }
        }

        // remove in-memory delete map
        delete serialized.deletedMap;
        serialized.lastSavedToRepo = window.apm.timestamp;

        exportObj.version = serialized.repoVersion;

        const designObj = Exporter.getSerializedJson(serialized);

        var JSZip = require("jszip");
        var zip = new JSZip();
        var exportFile = zip.folder(exportObj.application, { base64: true });

        if (exportObj.csvData.length > 0) {
            for (var cf = 0; cf < exportObj.csvData.length; cf++) {
                exportFile.file(designObj.csvData[cf].name + ".csv", designObj.csvData[cf].value);
            }
        }

        for (let type in exportObj.fillerPrompts) {
            for (var df = 0; df < exportObj.fillerPrompts[type].length; df++) {
                if (type !== "typing") {
                    exportObj.fillerPrompts[type][df].bargeable = "DISABLED";
                }
                else if (type === "typing") {
                    exportObj.fillerPrompts[type][df].bargeable = "ENABLED";
                }
                delete exportObj.fillerPrompts[type][df].lastSavedChange;
                delete exportObj.fillerPrompts[type][df].lastLocalChange;
                delete exportObj.fillerPrompts[type][df].createdTime;
            }

        }

        exportObj.version = serialized.repoVersion;

        let inputCsvHeaderFields = ['InputName', 'InputType', 'SamplePrompt', 'SamplePromptText', 'ModuleName', 'Sensitive', 'DTMF'];
        let inputsRowData = [inputCsvHeaderFields];
        let inputObjs = [];
        var Inputs = [];

        serialized.components.modules.forEach(module => {
            module.versions[1].flow.nodes.forEach(nod => {
                if (nod._class === ObjectClass.input) {
                    let promObjs = [];
                    for (var li = 0; li < module.versions[1].flow.links.length; li++) {
                        var link = module.versions[1].flow.links[li];
                        if (link.target === nod.id) {
                            var prIndex = module.versions[1].flow.nodes.findIndex(el => el.id === link.source);
                            if (module.versions[1].flow.nodes[prIndex]._class === ObjectClass.prompt) {
                                promObjs.push(module.versions[1].flow.nodes[prIndex]);
                            }
                        }
                        inputObjs.push({ input: nod, prompt: promObjs[0] });
                        Inputs.push(nod.props.parentComponentID);
                    }
                    var inputObjIndex = inputObjs.findIndex(object => object.input.name === nod.name);
                    if (inputObjIndex === -1) { inputObjs.push({ input: nod, prompt: promObjs[0], module: module.name }); }
                    Inputs.push(nod.props.parentComponentID);
                }
            });
        });

        var inputs = _.filter(serialized.components.objects, { _class: ObjectClass.input });
        inputs.forEach(input => {
            var inputIndex = Inputs.findIndex(inp => inp === input.id);
            if (inputIndex === -1) {
                inputObjs.push(({ input: input, prompt: undefined, module: undefined }));
            }
        });
        inputObjs.map(obj => {
            let row = [];
            row.push(obj.input.name);
            row.push(obj.input.props.config.inputType);

            if (obj.prompt) {
                row.push(obj.prompt.name);
                row.push(obj.prompt.props.config.text["en-US"]);
                row.push(obj.module);
            }
            else {
                row.push("See Diagram","","");
            }

            if(obj.input.props.config.sensitiveData) {
                row.push(obj.input.props.config.sensitiveData);
            } else {
                row.push('FALSE');
            }
            if(obj.input.props.config.DTMF) {
                row.push(obj.input.props.config.DTMF);
            } else {
                row.push('FALSE');
            }
            inputsRowData.push(row);
        });
        const newInputCsv = Papa.unparse(inputsRowData);
        const properties = window.apm.serialized.appProperties.filter(p => p.type === "variableBinding");
        let propStr = "";
        if (properties) {
            properties.forEach(p => {
                const variable = window.apm.getVariable(p.variable.split(".")[1]);
                if (variable) {
                    switch (variable.props.config.type) {
                    case "STRING":
                        propStr += `${p.config}=${p.default ? "\"" +p.default + "\"" : ""}\n`;
                        break;
                    case "BOOLEAN":
                        propStr += `${p.config}=${p.default === "false" ? false : !!p.default}\n`;
                        break;
                    default:
                        propStr += `${p.config}=${p.default ? p.default : ""}\n`;
                    }
                }
            });
        }
        const propFile = new Blob([propStr], { type: "text/plain;charset=utf-8" });

        exportFile.file(AvsFileName.inputs, newInputCsv);
        exportFile.file(AvsFileName.export, Exporter.createJsonBlob(exportObj));
        exportFile.file(AvsFileName.design, Exporter.createJsonBlob(designObj));
        exportFile.file(AvsFileName.dsids, Exporter.createJsonBlob(getDsids(serialized)));
        exportFile.file(AvsFileName.functions, functionObj);
        exportFile.file(AvsFileName.appCcxml, designObj.ccxml);
        exportFile.file(`${window.apm.serialized.client}_${window.apm.serialized.client}${window.apm.serialized.application}.properties`.toLowerCase(), propFile);
        exportFile.file(AvsFileName.unitTests, Exporter.createJsonBlob(serialized.unitTests));
        if (changeMapData) {
            exportFile.file(AvsFileName.changeLogMaps, Exporter.createJsonBlob(changeMapData));
        }

        // generate zip file, construct form data, post to /upload api
        const content = await zip.generateAsync({
            type: "blob",
            compression: "DEFLATE",
            compressionOptions: {
                level: 5
            }
        });

        var formData = new FormData();
        var { model: metaDataModel } = new MetaData(serialized);

        formData.append("file", content, {
            type: "application/zip"
        });

        // According to CORE-27001 applicationVersion can be empty
        formData.append(
            'properties',
            Exporter.createJsonBlob({
                "client": serialized.repoClient,
                "application": serialized.repoClient + serialized.repoApplication,
                "version": serialized.version,
                "applicationVersion": serialized.applicationVersion?serialized.applicationVersion:"",
                "mimeType": "application/zip",
                "metaData": {
                    "date": Date.now(),
                    ...metaDataModel
                },
                "type": "PACKAGE",
            })
        );
        const { branchId, version } = serialized;

        // Used while developing locally to check designs before saving them.
        const environment = DesignerSetting.s.env;
        const { isLocal, isProd } = getEnvironmentInfo(environment);
        if (isLocal && isProd) {
            console.log('Serialized to save:', designObj);
            const shouldSave = await globalModalsService.openConfirmationModal({
                children: 'Please double-check JSON in console prior to saving to PROD.'
            });
            if (!shouldSave) {
                return;
            }
        }

        try {
            await avsService.saveVersion(
                branchId,
                version,
                formData,
                progressMonitor
            );
            if (serialized.rootSettings.features?.intentManagement) {
                await intentService.saveCustomIntents(
                    intentService.getCustomIntentsFromSerialized(designObj),
                    branchId,
                    version
                );
            }
            window.apm.notification.post("success", "Project Saved To Repository Successfully");
            return serialized;
        } catch (error) {
            errorMessageService.addError(error);
            window.apm.notification.post(
                "error",
                "Error saving to Repository. Contact support.",
                false
            );
            w2popup.close();
            console.error(error);
            Utility.closeApplicationOverlay();
            return null;
        }
    }
}

export default SaveToRepository;
